/**
 * The action set a self-contained task-list card needs: complete (with the §8.4
 * celebration), undo, defer, star, edit, drop, delete — each followed by refetching the
 * card's own list and telling the page something changed, plus the §8.5 hold-in-place
 * bookkeeping for complete/undo (see `justDone.ts`).
 *
 * Shared by `TasksCard` and `StarredCard`, which used to be one page's worth of inline
 * handlers over a single `/api/briefing` fetch (`Briefing.tsx`, before D107). Splitting the
 * fetch into two independent cards meant duplicating this wiring twice or extracting it
 * once; extracted, since the two copies would otherwise drift the way `taskActions.ts`'s
 * docstring already describes happening to hand-rolled `TaskActions` elsewhere.
 *
 * Deliberately not `basicTaskActions`: that helper has no opinion about held rows because
 * `TaskDrawer` already holds its own place independently. A page-level card needs the hold,
 * the same way `Briefing.tsx` used to provide it directly.
 *
 * **Offline (§8.2, D157).** Complete, defer, edit and breakdown queue via `taskQueue.ts`
 * when `ApiError.isOffline`; every other action here (undo, star, drop, delete, next-action,
 * reorder) is deliberately still online-only — see DECISIONS.md D157 for the scope line and
 * why. A queued action skips the hold-in-place bookkeeping below entirely rather than
 * faking it: there is no real response yet to build a held row from, so this just posts a
 * notice and returns, leaving the row exactly as it was until the real state shows up.
 */

import { useCallback, useRef, useState } from "react";
import { ApiError, api, type Task, type RecurrenceRequest, type Tier } from "./api";
import type { TaskPatch } from "./components/TaskEditor";
import type { TaskActions } from "./components/TaskList";
import { anchorFor, mergeHeld, type HeldTask } from "./components/justDone";
import { celebrate, type BurstFn } from "./components/fireworksContext";
import { postNotice } from "./notices";
import { queueTaskComplete, queueTaskDefer, queueTaskSubtasks } from "./taskQueue";

/** How long the "something moved up" highlight (§8.6.1) stays on `justPromotedId`. */
const PROMOTION_HIGHLIGHT_MS = 1200;

export function useHeldTaskActions({
  reload,
  onChange,
  burst,
}: {
  /** Refetches this card's own list. Takes no arguments — a card that needs an energy
   * ceiling or similar closes over its own current value rather than this hook tracking it. */
  reload: () => Promise<void>;
  /** Called after every mutation, so a sibling card relying on the same tasks (Wins,
   * Gone-quiet, the other of Tasks/Starred) knows to refetch too. */
  onChange?: () => void | Promise<void>;
  burst: BurstFn;
}): {
  actions: TaskActions;
  /** Merge this card's freshly-fetched list with anything still held from before, and
   * remember it for the next completion's anchor. Call once per render with the raw list. */
  withHeld: (list: Task[]) => Task[];
} {
  const [justDone, setJustDone] = useState<HeldTask[]>([]);
  const [justPromotedId, setJustPromotedId] = useState<string | null>(null);
  const promotedTimer = useRef<number | undefined>(undefined);
  const listRef = useRef<Task[]>([]);

  const notify = useCallback(async () => {
    await reload();
    await onChange?.();
  }, [reload, onChange]);

  /**
   * Mark `id` as freshly promoted to Next Action (§8.6.1), for `justPromotedId`.
   *
   * Driven by the action that caused it, not detected by comparing renders — a component
   * watching its own props for a transition loses that memory the moment it remounts, which
   * happens more than it looks like it should (a parent's group can change the instant its
   * own `last_touched_at` bubbles, moving its row into a different section's DOM tree). A
   * prop a fresh mount can read immediately doesn't have that problem.
   */
  const flagPromoted = useCallback((id: string | null | undefined) => {
    if (!id) return;
    window.clearTimeout(promotedTimer.current);
    setJustPromotedId(id);
    promotedTimer.current = window.setTimeout(() => {
      setJustPromotedId(null);
    }, PROMOTION_HIGHLIGHT_MS);
  }, []);

  /**
   * `fresh` carries the fields that actually changed; `stale` supplies the ones that only
   * ever came from a join this card's own list request makes and a single-task fetch
   * (`GET /api/tasks/{id}`, `POST .../complete`'s own response) doesn't:
   * `area_name`/`project_name`, plus `group`/`days_since_touch`/`stale_after_days`, which
   * are computed by the briefing query rather than stored at all. Skipping this is exactly
   * what made a held or pinned row's area badge disappear until the next real reload
   * happened to replace it with a properly-joined copy.
   */
  function withDisplayFields(fresh: Task, stale: Task): Task {
    return {
      ...fresh,
      group: stale.group,
      days_since_touch: stale.days_since_touch,
      stale_after_days: stale.stale_after_days,
      area_name: stale.area_name,
      project_name: stale.project_name,
    };
  }

  /**
   * Fetch a subtask's parent fresh and pin it at its current on-screen position.
   *
   * Completing (or undoing) a subtask always touches its parent (§8.5 bubbling), and the
   * open-tasks list sorts stalest first — so a parent that just received progress would
   * otherwise jump to the *end* of its group on the very next fetch, purely because it is now
   * the freshest thing there. It never disappears and never finishes on a non-cascade
   * completion, so nothing about it *looks* like the kind of change hold-in-place already
   * covers; it just isn't where it was a second ago. Pinning it the same way a completed row
   * is held fixes both: a parent that also happened to complete (the last-subtask cascade,
   * D129) needs a held entry to not vanish; a parent that stays open needs one to not move.
   *
   * Returns the fresh parent (so the caller can also read its `next_action_id` for
   * `flagPromoted`), or `null` if `task` isn't a subtask.
   */
  const pinParent = useCallback(async (task: Task): Promise<Task | null> => {
    if (!task.parent_id) return null;
    const parent = listRef.current.find((t) => t.id === task.parent_id);
    if (!parent) return null;
    const after = anchorFor(listRef.current, parent);
    // Offline this single-task read has nothing mirrored to answer it — a parent is only ever
    // seen as part of a list, never fetched alone. Falling back to the copy already on screen
    // keeps the pin working; without this the read threw and took the whole action with it,
    // which is what stopped a subtask's undo from ever reaching its refresh (D169).
    const refetched = await api.task(parent.id).catch(() => null);
    const fresh = withDisplayFields(refetched ?? parent, parent);
    setJustDone((current) => [
      ...current.filter((h) => h.task.id !== fresh.id),
      { task: fresh, after },
    ]);
    return fresh;
  }, []);

  const complete = useCallback(
    async (task: Task, at?: { x: number; y: number }) => {
      // §8.4: completion is a celebration, once per point of activation cost. Fired before
      // the reload so it happens where the row was, not where the list ends up.
      if (at) celebrate(burst, at.x, at.y, task.energy);

      // A subtask is never a top-level row (§8.6.4) — it's reachable only through its
      // parent's own embedded `subtasks`, which `attach_subtasks` includes regardless of
      // status. It is therefore already "held" for free the moment it completes; giving it
      // a `justDone` entry of its own used to insert it a *second* time, as a phantom
      // top-level row with nowhere real to anchor (it was never in this list to begin
      // with), which is what made undoing it look like it left a blank row behind.
      const after = task.parent_id ? null : anchorFor(listRef.current, task);
      // Read before anything async runs, so it's what the Next Action pointer actually was
      // a moment ago — completing a subtask that wasn't it changes nothing about it, and
      // comparing against this is what tells that case apart from an actual auto-advance.
      const staleNextActionId = task.parent_id
        ? listRef.current.find((t) => t.id === task.parent_id)?.next_action_id
        : undefined;

      let result: { task: Task };
      try {
        result = await api.completeTask(task.id);
      } catch (err) {
        if (!(err instanceof ApiError && err.isOffline)) throw err;
        await queueTaskComplete(task.id);
        postNotice(`"${task.title}" is queued - it'll complete once you're back online.`);
        // Re-read, exactly as the online path does. The mirror has already been patched, so
        // this returns the task as done and the row holds in place with its undo — the queued
        // change and a server change reach the screen by the identical route (D166).
        await notify();
        return;
      }
      // Pinning has to read the parent's position *before* this fetch settles into the
      // render, which happens automatically since `pinParent` reads `listRef.current` (last
      // render's list) rather than anything this call has touched yet.
      const freshParent = await pinParent(task);

      if (!task.parent_id) {
        // Keep the grouping this list computed for it, so a ticked row stays in its own
        // section rather than dropping to "Everything else" on its way out.
        setJustDone((current) => [
          ...current.filter((h) => h.task.id !== task.id),
          { task: withDisplayFields(result.task, task), after },
        ]);
      }
      if (freshParent && freshParent.next_action_id !== staleNextActionId) {
        // The silent auto-advance (§8.6.1) already happened server-side by the time
        // `pinParent` fetched this — if it promoted a different subtask, that's the row to
        // highlight, not `task` itself (which just finished, not started).
        flagPromoted(freshParent.next_action_id);
      }
      await notify();
    },
    [notify, burst, pinParent, flagPromoted],
  );

  const undo = useCallback(
    async (task: Task) => {
      const after = task.parent_id ? null : anchorFor(listRef.current, task);
      // Emits `reopened`, which cancels the win for the day (§8.4) — so an accidental
      // tick leaves nothing behind.
      // Queued and mirrored automatically when offline (`queueable` in `api.ts`), so undo
      // works with no connection — a tick made offline must not be irreversible until one
      // returns (D166).
      await api.patchTask(task.id, { status: "open" });

      if (task.parent_id) {
        setJustDone((current) => current.filter((h) => h.task.id !== task.id));
      } else {
        // Hold-in-place applies to an undo the same way it applies to a completion (§8.5):
        // a row just acted on should stay visible until the next real page load, even if it
        // would otherwise fail a filter unrelated to why it was touched. Reported directly —
        // undoing a subtask cascade puts the parent's Next Action back to whatever the
        // trigger subtask was, and if that costs more than the energy slider currently
        // allows, an unpinned reopen vanished it on the very next fetch: not done, not
        // filtered by anything to do with the undo itself, just gone before it could be
        // looked at again (or undone right back, if reopening it was itself the mistake).
        // Offline this single-task read may have nothing mirrored to answer it — the row
        // was only ever seen as part of a list. The local copy, reopened, is exactly as good
        // for holding its place, so fall back to it rather than failing the undo.
        const reopened = await api.task(task.id).catch(() => null);
        const fresh = reopened
          ? withDisplayFields(reopened, task)
          : { ...task, status: "open" as const, completed_at: null };
        setJustDone((current) => [
          ...current.filter((h) => h.task.id !== task.id),
          { task: fresh, after },
        ]);
      }
      // §8.6.3: reopening a subtask restores its parent's staleness too, which can move it
      // the same way completing one does — pin it for the same reason.
      await pinParent(task);
      await notify();
    },
    [notify, pinParent],
  );

  const defer = useCallback(
    async (task: Task) => {
      try {
        await api.deferTask(task.id);
      } catch (err) {
        if (!(err instanceof ApiError && err.isOffline)) throw err;
        await queueTaskDefer(task.id);
        postNotice(`"${task.title}" is queued to snooze - it'll apply once you're back online.`);
        await notify();
        return;
      }
      await notify();
    },
    [notify],
  );

  const star = useCallback(
    async (task: Task) => {
      await api.patchTask(task.id, { starred: !task.starred });
      await notify();
    },
    [notify],
  );

  const edit = useCallback(
    async (task: Task, patch: TaskPatch, recurrence?: RecurrenceRequest) => {
      // Both queue themselves when offline (`queueable` in `api.ts`). Unlike adding a *new*
      // task with Repeat — which needs the id the create has not returned yet, and is still
      // refused in `QuickAddCard` — changing an existing task's rule is a standalone call
      // against an id that already exists, so there is nothing to chain (D168).
      if (Object.keys(patch).length > 0) await api.patchTask(task.id, patch);
      if (recurrence !== undefined) await api.setRecurrence(task.id, recurrence);
      await notify();
    },
    [notify],
  );

  const drop = useCallback(
    async (task: Task) => {
      // §7's `dropped` status, not a DELETE — see TaskActions.onDrop.
      await api.patchTask(task.id, { status: "dropped" });
      await notify();
    },
    [notify],
  );

  const remove = useCallback(
    async (task: Task) => {
      await api.deleteTask(task.id);
      await notify();
    },
    [notify],
  );

  const breakdown = useCallback(
    async (task: Task, titles: string[]) => {
      // The server dispatches create vs. split by whether `task.id` already has a parent
      // (§8.6.2, §10) — one call serves both, and so does the offline queue (taskQueue.ts).
      let subtasks: Task[];
      try {
        ({ subtasks } = await api.createSubtasks(task.id, titles));
      } catch (err) {
        if (!(err instanceof ApiError && err.isOffline)) throw err;
        const queued = await queueTaskSubtasks(task, titles);
        postNotice(`Queued - these steps will be added once you're back online.`);
        await notify();
        // The provisional steps, so a split undone before it syncs knows what to remove —
        // they carry the ids the queue will send (§5.1), which is what makes that possible.
        return queued.kind === "subtasks"
          ? queued.ids.map((id, i) => ({ ...task, id, title: queued.titles[i], parent_id: task.id }))
          : undefined;
      }
      // A split that replaced the Next Action promotes the first replacement (§8.6.2),
      // silently — the same case `complete`'s auto-advance flags.
      flagPromoted(subtasks.find((s) => s.is_next_action)?.id);
      await notify();
      return subtasks;
    },
    [notify, flagPromoted],
  );

  // Online-only, unlike `breakdown` above — there's no queueable shape for "ask a model
  // something" the way there is for a titles list already typed out (§8.2, D157). Errors
  // (including offline) propagate to `AiBreakdownButton`, which is what shows them.
  const aiBreakdown = useCallback(
    async (task: Task, tier: Tier) => {
      const { subtasks } = await api.aiBreakdown(task.id, tier);
      flagPromoted(subtasks.find((s) => s.is_next_action)?.id);
      await notify();
      return subtasks;
    },
    [notify, flagPromoted],
  );

  const undoBreakdown = useCallback(
    async (
      parent: Task,
      undo: { created: string[]; restore?: { id: string; title: string; sortOrder: number } },
    ) => {
      await api.undoBreakdown(parent.id, {
        created: undo.created,
        restore_id: undo.restore?.id,
        title: undo.restore?.title,
        sort_order: undo.restore?.sortOrder,
      });
      if (undo.restore) flagPromoted(undo.restore.id);
      await notify();
    },
    [notify, flagPromoted],
  );

  const setNextAction = useCallback(
    async (parent: Task, subtaskId: string) => {
      await api.setNextAction(parent.id, subtaskId);
      flagPromoted(subtaskId);
      await notify();
    },
    [notify, flagPromoted],
  );

  const reorderSubtasks = useCallback(
    async (parent: Task, ids: string[]) => {
      await api.reorderSubtasks(parent.id, ids);
      await notify();
    },
    [notify],
  );

  const actions: TaskActions = {
    onComplete: complete,
    onDefer: defer,
    onStar: star,
    onEdit: edit,
    onDrop: drop,
    onDelete: remove,
    onUndo: undo,
    onBreakdown: breakdown,
    onAiBreakdown: aiBreakdown,
    onUndoBreakdown: undoBreakdown,
    onSetNextAction: setNextAction,
    onReorderSubtasks: reorderSubtasks,
    justPromotedId,
  };

  const withHeld = useCallback(
    (list: Task[]) => {
      const merged = mergeHeld(list, justDone);
      // Recorded after merging so a second tick anchors against the first one's held row.
      listRef.current = merged;
      return merged;
    },
    [justDone],
  );

  return { actions, withHeld };
}
